"use client"

import { useCallback, useEffect, useRef, useState, type ReactNode } from "react"
import { createPortal } from "react-dom"
import Link from "next/link"
import { useTranslations } from "@/lib/intl/use-translations"
import { cn } from "@/lib/utils"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { track } from "@/lib/analytics"
import { CopyIcon, DownloadIcon, ArrowUpRightIcon } from "@/components/ui/icons"

// ── Official brand wordmark ──────────────────────────────────────────────
// Canonical paths from the brand kit (Drive › Assets_Ahead Health › 1. LOGO ›
// STATIC › LOGOTYPE › DIGITAL). The on-screen nav/footer logo is a smaller
// inline SVG; the asset we hand out here is the full-resolution wordmark.
// Geometry is identical across the four brand colours — only the fill differs,
// so we keep one path set and recolour at export time.
const BRAND_VIEWBOX = "0 0 1407.44 357.86"
const BRAND_PATHS = [
  "M159.09,45.04l72.45,179.66H86.16L159.09,45.04ZM140.98,9.8L0,352.45h34.76l39.65-97.9h168.88l39.65,97.9h36.72L178.67,9.8h-37.69Z",
  "M468.66,93.01c-40.14,0-69.52,20.56-83.71,47.97V0h-32.3v352.45h32.3v-146.37c0-55.8,35.74-84.68,74.9-84.68s67.55,24.96,67.55,74.89v156.15h32.31v-160.07c0-67.55-37.21-99.37-91.05-99.37",
  "M723.07,93.01c-62.17,0-120.42,47.97-120.42,130.7s53.85,134.12,122.86,134.12c53.36,0,100.35-27.41,115.03-77.84h-32.31c-14.19,32.8-43.56,49.94-81.74,49.94-50.91,0-88.6-40.63-91.04-97.41h206.57v-8.81c0-85.67-55.8-130.7-118.95-130.7M635.46,206.09c3.92-53.36,42.59-85.67,87.13-85.67s83.71,32.31,86.15,85.67h-173.28Z",
  "M1375.14,0v147.83c-13.71-31.33-47.97-54.82-91.54-54.82-62.17,0-116.99,48.95-116.99,130.7s53.35,134.12,116.99,134.12c43.57,0,78.32-26.92,91.54-59.72v54.34h32.3V0h-32.3ZM1288,329.94c-49.93,0-88.11-40.63-88.11-106.22s38.18-102.8,88.11-102.8c44.55,0,87.62,32.8,87.62,102.8s-43.56,106.22-87.62,106.22",
  "M1065.96,95.77h-183.34v32.4l182.06-4.49c16.47-.41,29.95,13.01,29.62,29.49l-3.96,199.29h31.81v-200.49c0-31.03-25.16-56.2-56.2-56.2",
  "M1028.04,171.01l-151.07,163.29,22.98,23.56,151.94-164.84c6.14-6.65,5.64-17.03-1.1-23.07-6.6-5.92-16.72-5.45-22.74,1.06",
]

const BRAND_COLORS = [
  { id: "white", hex: "#ffffff", labelKey: "logoMenuColorWhite" },
  { id: "darkGreen", hex: "#153519", labelKey: "logoMenuColorDarkGreen" },
  { id: "aubergine", hex: "#48214a", labelKey: "logoMenuColorAubergine" },
  { id: "darkBlue", hex: "#182d56", labelKey: "logoMenuColorDarkBlue" },
] as const
type BrandColorId = (typeof BRAND_COLORS)[number]["id"]

const PNG_HEIGHT = 512 // exported PNG height in px; width derives from the viewBox ratio

function buildSvgMarkup(hex: string): string {
  const paths = BRAND_PATHS.map((d) => `<path d="${d}"/>`).join("")
  return (
    `<?xml version="1.0" encoding="UTF-8"?>\n` +
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${BRAND_VIEWBOX}" ` +
    `width="1407.44" height="357.86" fill="${hex}" role="img" ` +
    `aria-label="Ahead Health">${paths}</svg>`
  )
}

function triggerDownload(filename: string, blob: Blob) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

/** Perceived-luminance test on a computed `rgb(...)` string. Picks the
 *  default swatch that matches how the logo currently renders (white on the
 *  transparent hero nav, dark elsewhere). */
function isLightColor(rgb: string): boolean {
  const parts = rgb.match(/\d+(\.\d+)?/g)
  if (!parts || parts.length < 3) return false
  const [r, g, b] = parts.map(Number)
  return 0.299 * r + 0.587 * g + 0.114 * b > 140
}

type MenuState = { x: number; y: number }

/**
 * Wraps the site logo with a brand-asset menu: right-click (or select +
 * copy) the logo to copy/download the official wordmark as SVG or PNG in any
 * of the four brand colours. Left-clicking the logo still navigates home, so
 * the wrapper is transparent to normal use.
 */
export function LogoBrandMenu({ children }: { children: ReactNode }) {
  const t = useTranslations("ui")
  const localePath = useLocalePath()
  const wrapRef = useRef<HTMLSpanElement>(null)
  const popRef = useRef<HTMLDivElement>(null)
  const [menu, setMenu] = useState<MenuState | null>(null)
  const [visible, setVisible] = useState(false)
  const [colorId, setColorId] = useState<BrandColorId>("darkGreen")
  const [copied, setCopied] = useState(false)

  const activeHex =
    BRAND_COLORS.find((c) => c.id === colorId)?.hex ?? BRAND_COLORS[1].hex

  const pickContextualDefault = useCallback(() => {
    const el = wrapRef.current
    if (!el) return
    const rendered = getComputedStyle(el).color
    setColorId(isLightColor(rendered) ? "white" : "darkGreen")
  }, [])

  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  )
  const timers = useRef(new Set<ReturnType<typeof setTimeout>>())

  // No pending timer may outlive the component (it would set state on an
  // unmounted tree).
  useEffect(() => {
    const pending = timers.current
    return () => {
      clearTimeout(closeTimer.current)
      pending.forEach(clearTimeout)
    }
  }, [])

  const later = useCallback((fn: () => void, ms: number) => {
    const id = setTimeout(() => {
      timers.current.delete(id)
      fn()
    }, ms)
    timers.current.add(id)
  }, [])

  const close = useCallback(() => {
    setVisible(false)
    clearTimeout(closeTimer.current)
    closeTimer.current = setTimeout(() => setMenu(null), 150)
  }, [])

  const openAt = useCallback(
    (x: number, y: number) => {
      clearTimeout(closeTimer.current)
      pickContextualDefault()
      setMenu({ x, y })
    },
    [pickContextualDefault]
  )

  const onContextMenu = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      openAt(e.clientX, e.clientY)
    },
    [openAt]
  )

  // Reposition once mounted so the card never overflows the viewport, then
  // fade in on the next frame.
  useEffect(() => {
    if (!menu) return
    const raf = requestAnimationFrame(() => {
      const card = popRef.current
      if (card) {
        const r = card.getBoundingClientRect()
        const x = Math.min(menu.x, window.innerWidth - r.width - 12)
        const y = Math.min(menu.y, window.innerHeight - r.height - 12)
        card.style.left = `${Math.max(12, x)}px`
        card.style.top = `${Math.max(12, y)}px`
      }
      setVisible(true)
    })
    return () => cancelAnimationFrame(raf)
  }, [menu])

  // Dismissal: outside click, Escape, scroll.
  useEffect(() => {
    if (!menu) return
    const onPointerDown = (e: PointerEvent) => {
      if (popRef.current && !popRef.current.contains(e.target as Node)) close()
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close()
    }
    document.addEventListener("pointerdown", onPointerDown, true)
    document.addEventListener("keydown", onKey, true)
    window.addEventListener("scroll", close, true)
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true)
      document.removeEventListener("keydown", onKey, true)
      window.removeEventListener("scroll", close, true)
    }
  }, [menu, close])

  // Select-the-logo-then-Cmd/Ctrl+C → copy the SVG instead of empty text.
  useEffect(() => {
    const onCopy = (e: ClipboardEvent) => {
      const el = wrapRef.current
      const sel = window.getSelection()
      if (!el || !sel || sel.isCollapsed || !el.isConnected) return
      if (!sel.containsNode(el, true)) return
      e.preventDefault()
      const hex = isLightColor(getComputedStyle(el).color)
        ? "#ffffff"
        : "#153519"
      e.clipboardData?.setData("text/plain", buildSvgMarkup(hex))
      track.logoCopied("svg", hex === "#ffffff" ? "white" : "darkGreen")
    }
    document.addEventListener("copy", onCopy)
    return () => document.removeEventListener("copy", onCopy)
  }, [])

  const flashCopied = useCallback(() => {
    setCopied(true)
    later(() => setCopied(false), 1600)
  }, [later])

  const onCopySvg = useCallback(async () => {
    await navigator.clipboard.writeText(buildSvgMarkup(activeHex))
    track.logoCopied("svg", colorId)
    flashCopied()
    later(close, 600)
  }, [activeHex, colorId, flashCopied, close, later])

  const onDownloadSvg = useCallback(() => {
    triggerDownload(
      `ahead-health-logo-${colorId}.svg`,
      new Blob([buildSvgMarkup(activeHex)], { type: "image/svg+xml" })
    )
    track.logoDownloaded("svg", colorId)
    close()
  }, [activeHex, colorId, close])

  const onDownloadPng = useCallback(() => {
    const markup = buildSvgMarkup(activeHex)
    const [, , w, h] = BRAND_VIEWBOX.split(/\s+/).map(Number)
    const height = PNG_HEIGHT
    const width = Math.round((w / h) * height)
    const img = new Image()
    img.onload = () => {
      const canvas = document.createElement("canvas")
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext("2d")
      if (!ctx) return
      ctx.drawImage(img, 0, 0, width, height)
      canvas.toBlob((blob) => {
        if (blob) triggerDownload(`ahead-health-logo-${colorId}.png`, blob)
      }, "image/png")
    }
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`
    track.logoDownloaded("png", colorId)
    close()
  }, [activeHex, colorId, close])

  return (
    <>
      <span ref={wrapRef} onContextMenu={onContextMenu} className="inline-flex">
        {children}
      </span>

      {menu &&
        createPortal(
          <div
            ref={popRef}
            role="menu"
            aria-label={t("logoMenuTitle")}
            style={{ left: menu.x, top: menu.y }}
            className={cn(
              "fixed z-[100] min-w-[228px] rounded-[16px] bg-white p-1.5",
              "shadow-[0_12px_40px_rgba(11,31,42,0.18)]",
              "transition-[opacity,transform] duration-150 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none",
              visible ? "opacity-100" : "translate-y-1 opacity-0"
            )}
          >
            <div className="type-label-sm px-2.5 pt-2 pb-1 text-neutral-400 uppercase">
              {t("logoMenuTitle")}
            </div>

            {/* Colour swatches */}
            <div
              role="group"
              aria-label={t("logoMenuColorLabel")}
              className="flex items-center gap-2 px-2.5 py-1.5"
            >
              {BRAND_COLORS.map((c) => {
                const selected = c.id === colorId
                return (
                  <button
                    key={c.id}
                    type="button"
                    role="menuitemradio"
                    aria-checked={selected}
                    aria-label={t(c.labelKey)}
                    title={t(c.labelKey)}
                    onClick={() => setColorId(c.id)}
                    style={{ backgroundColor: c.hex }}
                    className={cn(
                      "h-6 w-6 rounded-full transition-transform motion-reduce:transition-none",
                      c.id === "white" && "ring-1 ring-neutral-200",
                      selected
                        ? "ring-2 ring-neutral-900 ring-offset-2"
                        : "hover:scale-110"
                    )}
                  />
                )
              })}
            </div>

            <div className="my-1.5 h-px bg-neutral-100" />

            <MenuItem
              icon={<CopyIcon className="h-[18px] w-[18px] text-neutral-500" />}
              label={copied ? t("logoMenuCopied") : t("logoMenuCopySvg")}
              onClick={onCopySvg}
            />
            <MenuItem
              icon={
                <DownloadIcon className="h-[18px] w-[18px] text-neutral-500" />
              }
              label={t("logoMenuDownloadSvg")}
              onClick={onDownloadSvg}
            />
            <MenuItem
              icon={
                <DownloadIcon className="h-[18px] w-[18px] text-neutral-500" />
              }
              label={t("logoMenuDownloadPng")}
              onClick={onDownloadPng}
            />

            <div className="my-1.5 h-px bg-neutral-100" />

            <Link
              href={localePath("/about")}
              role="menuitem"
              onClick={close}
              className="type-body-md flex w-full items-center gap-2.5 rounded-[9px] px-2.5 py-2 text-left text-neutral-900 transition-colors hover:bg-neutral-50 motion-reduce:transition-none"
            >
              <ArrowUpRightIcon className="h-[18px] w-[18px] text-neutral-500" />
              {t("logoMenuAbout")}
            </Link>
          </div>,
          document.body
        )}
    </>
  )
}

function MenuItem({
  icon,
  label,
  onClick,
}: {
  icon: ReactNode
  label: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className="type-body-md flex w-full items-center gap-2.5 rounded-[9px] px-2.5 py-2 text-left text-neutral-900 transition-colors hover:bg-neutral-50 motion-reduce:transition-none"
    >
      {icon}
      {label}
    </button>
  )
}
